import { useTranslation } from "react-i18next";

import {
  AssetChips,
  RiskDot,
  RobotAvatar,
  asMarket,
  asRiskLevel,
  robotColor,
  robotTransitionName,
  type RobotDetail,
  type ShowcaseReturns,
} from "@/entities/robot";
import { formatPercent, percentTone, useSharedElementName } from "@/shared/lib";

/**
 * Шапка экрана робота (ТЗ §5 и §6).
 *
 * Собрана из бывшей `RobotShowcaseCard`: подложка — градиент от фона к цвету
 * робота на 6%, поэтому оттенок вычисляется из акцента, а не фиксируется
 * зелёным.
 *
 * Крупная цифра — доходность за 12 месяцев: тот же показатель, что на правом
 * краю графика ниже и во вторичной строке карточки каталога. Подпись под ней
 * обязана говорить о ПРОШЛОМ («Исторический результат за последние 12
 * месяцев»), а не о доходности вообще: ТЗ §2 требует не обещать будущее.
 *
 * Под ней — лесенка 30D/3M/6M/12M, где каждый процент стоит под своим
 * периодом. Сноски о расхождении с каталогом здесь больше нет: обе витрины
 * берут `performance` из одного расчёта, и объяснять нечего.
 */
export function RobotHero({ robot, returns }: { robot: RobotDetail; returns: ShowcaseReturns }) {
  const { t } = useTranslation();
  const color = robotColor(robot);
  // Имя непустое только когда на экран пришли нажатием по строке каталога —
  // тогда две карточки перетекают одна в другую (FE-13).
  const transitionName = useSharedElementName(robotTransitionName(robot.id));

  return (
    <section
      className="flex flex-col gap-4 rounded-3xl border p-5"
      style={{
        borderColor: color,
        backgroundImage: `linear-gradient(to right, var(--color-background-elevated), color-mix(in srgb, ${color} 6%, transparent))`,
        ...(transitionName ? { viewTransitionName: transitionName } : {}),
      }}
    >
      <header className="flex items-start justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2.5">
          <RobotAvatar robot={robot} size={36} />
          <span className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-h3 font-semibold text-text-primary">{robot.name}</span>
            {robot.tagline ? (
              <span className="truncate text-caption font-medium text-text-secondary">
                {robot.tagline}
              </span>
            ) : null}
          </span>
        </span>
        <span
          className="shrink-0 rounded-full border border-border-strong bg-background-raised p-[7px] text-micro font-semibold uppercase"
          style={{ color }}
        >
          {t(`robots.market.${asMarket(robot.market)}`)}
        </span>
      </header>

      <span className="flex items-center gap-2">
        <RiskDot risk={robot.risk_level} />
        <span className="text-micro font-semibold uppercase text-text-secondary">
          {t(`robots.risk.${asRiskLevel(robot.risk_level)}`)}
        </span>
      </span>

      <AssetChips assets={robot.assets} max={6} />

      <div className="flex flex-col gap-1">
        <span className={`text-display font-semibold ${percentTone(returns.m12)}`}>
          {formatPercent(returns.m12, { sign: "always" })}
        </span>
        <span className="text-caption font-normal text-text-secondary">
          {t("robots.analytics.returns.caption")}
        </span>
      </div>

      {/* Лесенка периодов. Горизонтальный скролл, а не перенос: четыре пары
          «подпись + число» на 320 px в две строки читаются как таблица без
          заголовка. */}
      <dl className="no-scrollbar -mx-1 flex gap-5 overflow-x-auto px-1">
        {(["d30", "m3", "m6", "m12"] as const).map((key) => (
          <div key={key} className="flex shrink-0 flex-col gap-[3px]">
            <dt className="text-micro font-medium uppercase text-text-tertiary">
              {t(`robots.analytics.returns.${key}`)}
            </dt>
            <dd className={`text-body font-semibold ${percentTone(returns[key])}`}>
              {formatPercent(returns[key], { sign: "always" })}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
